/* Install composition api first */
import "@/installCompositionApi";

/* Framework + plugins imports */
import Vue from "vue";
import Vuex from "vuex";
import VueSessionStorage from "vue-sessionstorage";
import VueTextareaAutosize from "vue-textarea-autosize";
import VueLinkify from "vue-linkify";
import FineMq from "fine-mq";
import { createApp, ref, computed } from "@vue/composition-api";
import { provideI36n } from "@jota-one/i36n/src/index.vue2";

/* Global scope imports */
import "@/helpers/directives";
import "@/helpers/polyfill";

/* Global css scope imports */
import "@/assets/css/tailwind.css";

/* Local imports */
import { EVENT, listenToCustomEvent } from "@/helpers/customEvents";
import { isDeactivated } from "@/helpers/helpers";
import http from "@/helpers/http";
import { idle } from "@/helpers/inactivityTracker";
import ChatApi from "@/api/sanitasChat";
import store from "@/store";

/* App import */
import SanitasChat from "@/SanitasChat";

/* Vue global config */
Vue.config.productionTip = false;

if (process.env.ENABLE_VUE_DEVTOOLS) {
  Vue.config.devtools = true;
}

/* Init plugins */
Vue.use(Vuex);
Vue.use(FineMq, {
  aliases: {
    headerIsMobile: 490,
    headerIsTablet: [491, 1199],
    headerIsDesktop: [1200],
    xs: [0, 543],
    sm: [544, 767],
    md: [768, 1023],
    lg: [1024, 1199],
    xl: [1200],
    landscape: "(orientation: landscape)",
    isMobile: [0, 767],
    isTablet: [768, 1023],
    isDesktop: [1024],
  },
});
Vue.use(VueSessionStorage);
Vue.use(VueTextareaAutosize);
Vue.directive("linkified", VueLinkify);

/* App global config */
const chatAppClass = "sanchat-wrapper";
const chatAppDomId = "sanitas-chat";
let loading = false;
let loaded = false;

/** -------------- Create a http client for the MS -------------------- */
if (!process.env.VUE_APP_LOCAL) {
  http.createHateoas({
    name: "ms",
    axiosConfig: {
      baseURL: window._sanChatConfig.baseUrl,
    },
    rootEndpoint: `/index?channel=${window._sanChatConfig.channel}`,
    ...(window._sanChatConfig.setMockUser
      ? {
          headersMap: {
            headers: {
              "X-Username": "mockUsername",
              "X-User-Roles": "xUserRoles",
              "X-User-PartnerContract": "xUserPartner",
              "X-User-HeadContract": "xUserHead",
              "X-User-UID": "xUserUid",
            },
            source: window._sanChatConfig.mockUser,
          },
        }
      : {}),
  });
}

/** -------------- Create a default http client ------------------------ */
http.create({
  axiosConfig: {
    baseURL: "/",
  },
});

if (!process.env.VUE_APP_LOCAL) {
  Vue.prototype.$chatApi = new ChatApi(window._sanChatConfig);
}

if (
  window._sanChatConfig &&
  (window._sanChatConfig.trackIdle === "true" ||
    window._sanChatConfig.trackIdle === true) &&
  !sessionStorage.isNudgingDisabled
) {
  idle.start();
}

const showKey = ref(false);

window.i36nChat = {
  toggleKeys: function () {
    console.log(showKey.value);
    showKey.value = !showKey.value;
  },
};

const initChatApp = async () => {
  if (loading || loaded) {
    return;
  }

  loading = true;

  if (process.env.VUE_APP_LOCAL) {
    http.createHateoas({
      name: "ms",
      overwrite: true,
      axiosConfig: {
        baseURL: window._sanChatConfig.baseUrl,
      },
      rootEndpoint: `/index?channel=${window._sanChatConfig.channel}`,
      ...(window._sanChatConfig.setMockUser
        ? {
            headersMap: {
              headers: {
                "X-Username": "mockUsername",
                "X-User-Roles": "xUserRoles",
                "X-User-PartnerContract": "xUserPartner",
                "X-User-HeadContract": "xUserHead",
                "X-User-UID": "xUserUid",
              },
              source: window._sanChatConfig.mockUser,
            },
          }
        : {}),
    });

    Vue.prototype.$chatApi = new ChatApi(window._sanChatConfig);
  }

  if (process.env.VUE_APP_DEBUG_API) {
    window.$chatApi = Vue.prototype.$chatApi;
  }

  if (process.env.VUE_APP_DEBUG_STORE) {
    window.$chatStore = store;
  }

  await store.dispatch("uistate/init", {
    config: window._sanChatConfig,
  });

  if (!isDeactivated(window._sanChatConfig)) {
    await store.dispatch("conversation/setIsNonCustomer");
    await store.dispatch("restoreSession");
  }

  window.addEventListener("beforeunload", () => {
    Vue.prototype.$chatApi.disconnect();
  });

  const app = createApp({
    store,
    components: { SanitasChat },
    template: `<${chatAppDomId} class="${chatAppClass}" style="display:none"></${chatAppDomId}>`,
    setup() {
      const lang = computed(() => store.state.uistate.lang);
      provideI36n(lang, { load: loadLabels, showKey });
    },
    created() {
      listenToCustomEvent(EVENT.LISTEN_TO.DESTROY, () => {
        this.$destroy();
        Vue.prototype.$chatApi.disconnect();
        if (document.getElementsByClassName(chatAppClass).length > 0) {
          document.getElementsByClassName(chatAppClass)[0].remove();
        }
        if (!document.getElementById(chatAppDomId)) {
          createChatDOM();
        }

        loading = false;
        loaded = false;
      });
    },
  });
  app.mount(`#${chatAppDomId}`);
  loaded = true;
};

const createChatDOM = () => {
  const div = document.createElement("div");
  div.setAttribute("id", chatAppDomId);
  document.body.appendChild(div);
};

const loadLabels = async (lang) => {
  store.commit("uistate/SET_LOADING_LABELS", true);

  const response = await Promise.all([
    import(`./i18n/${lang}.json`),
    Vue.prototype.$chatApi.getUiTexts(lang),
  ]).then((results) => ({ ...results[0].default, ...results[1] }));

  store.commit("uistate/SET_LOADING_LABELS", false);

  return response;
};

const config = window._sanChatConfig;

if (process.env.VUE_APP_LOCAL) {
  const ChatEnduserDemo = () =>
    import(
      /* webpackChunkName: "app-demo" */
      "@/Demo"
    );

  createApp({
    components: { ChatEnduserDemo },
    template: "<chat-enduser-demo></chat-enduser-demo>",
    created() {
      window.initChatApp = initChatApp;
    },
  }).mount("#demo");
} else {
  if (config.isCustomerFlow) {
    if (config.displayMode === "webview") {
      Vue.prototype.$chatApi.getChatAvailable("de").then((result) => {
        if (result?.error?.response?.status !== 401) {
          initChatApp();
        }
      });
    } else {
      listenToCustomEvent(EVENT.LISTEN_TO.SHOW_FAB, () => {
        if (loaded) {
          return;
        }
        initChatApp();
      });
    }
  } else {
    initChatApp();
  }
}
